import React, { useState, useMemo, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import styled from '@emotion/styled';
import InfoTip from './InfoTip';

const COLORS = [
  '#1976D2',
  '#00B8D4',
  '#5C6BC0',
  '#8BC34A',
  '#880E4F',
  '#006A71',
  '#1E2D3D',
  '#E0E4E8',
  '#F28E2B',
  '#E15759',
  '#76B7B2',
  '#59A14F',
];
const LOT_COLORS: Record<string, string> = {
  '1L': '#1976D2',
  '2L': '#4CB847',
  '3L': '#F28E2B',
  '4L+': '#E12E40',
};
const FORM_COLORS: Record<string, string> = {
  IV: '#1976D2',
  SC: '#4CB847',
  Oral: '#F28E2B',
  INJECTION: '#5C6BC0',
};

const tooltipStyle = {
  background: '#ffffff',
  border: '1px solid #e5e5e5',
  fontSize: 11,
  borderRadius: 6,
};

const TIPS = {
  brand:
    'For each calendar month, we count unique patients who had at least one claim on each molecule. The LOT pill scopes the count to L1, L2, or L3+ claims only.',
  regimen:
    'Per month, unique patients on each regimen are converted to a share of the SELECTED regimens. Adding/removing a chip rescales the stack.',
  formulation:
    'For the chosen molecule, we count unique patients per month for each product form (IV / SC / Oral / Injection) and stack them as 100% shares.',
  sankey:
    'For each patient, the FIRST regimen per line of therapy (1L, 2L, 3L, 4L+). Each ribbon shows how many patients moved from a 1L regimen to a 2L regimen, etc.',
};

/* ── Shared controls ──────────────────────────────────────────────────── */

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;
`;

const ChipBtn = styled.button<{ $active: boolean; $color: string }>`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  font-family: inherit;
  border: 1px solid ${(p) => (p.$active ? 'transparent' : '#d3d3d3')};
  cursor: pointer;
  transition: all 80ms;
  background: ${(p) => (p.$active ? p.$color : '#ffffff')};
  color: ${(p) => (p.$active ? '#fff' : '#626262')};
  &:hover {
    opacity: 0.85;
  }
`;

function MultiSelectChips({
  items,
  selected,
  onChange,
  allItems,
  max = 12,
}: {
  items: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  allItems: string[];
  max?: number;
}): React.JSX.Element {
  const display = items.slice(0, max);
  const toggle = (item: string): void => {
    const next = selected.includes(item) ? selected.filter((x) => x !== item) : [...selected, item];
    onChange(next);
  };
  return (
    <ChipRow>
      {display.map((item) => {
        const on = selected.includes(item);
        const idx = allItems.indexOf(item);
        const color = COLORS[idx % COLORS.length];
        return (
          <ChipBtn key={item} $active={on} $color={color} onClick={() => toggle(item)}>
            {item}
          </ChipBtn>
        );
      })}
    </ChipRow>
  );
}

const LotWrap = styled.div`
  display: flex;
  gap: 3px;
`;
const LotBtn = styled.button<{ $active: boolean }>`
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  font-family: inherit;
  border: 1px solid ${(p) => (p.$active ? '#28AAE1' : '#d3d3d3')};
  cursor: pointer;
  transition: all 0.15s;
  background: ${(p) => (p.$active ? '#28AAE1' : '#ffffff')};
  color: ${(p) => (p.$active ? '#fff' : '#626262')};
  &:hover {
    border-color: #28aae1;
  }
`;

function LotPills({ value, onChange }: { value: string; onChange: (v: string) => void }): React.JSX.Element {
  const opts = [
    { id: 'all', label: 'Total' },
    { id: 'line1', label: 'L1' },
    { id: 'line2', label: 'L2' },
    { id: 'line3+', label: 'L3+' },
  ];
  return (
    <LotWrap>
      {opts.map((o) => (
        <LotBtn key={o.id} $active={value === o.id} onClick={() => onChange(o.id)}>
          {o.label}
        </LotBtn>
      ))}
    </LotWrap>
  );
}

/* ── Chart boxes ──────────────────────────────────────────────────────── */

const ChartBox = styled.div`
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 16px;
`;

const IVChartBox = styled.div`
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
`;

const BrandSelect = styled.select`
  font-size: 11px;
  font-family: inherit;
  padding: 4px 8px;
  border: 1px solid #d3d3d3;
  border-radius: 6px;
  background: #ffffff;
  color: #323232;
  cursor: pointer;
  outline: none;
  &:hover {
    border-color: #28aae1;
  }
  &:focus {
    border-color: #28aae1;
    box-shadow: 0 0 0 2px rgba(40, 170, 225, 0.15);
  }
`;

/* ── Brand Trends (line chart) ────────────────────────────────────────── */

function BrandTrendsChart({ data }: { data: Record<string, unknown> }): React.JSX.Element {
  const [lot, setLot] = useState('all');
  const ranking = (data.brand_ranking ?? []) as string[];
  const [selected, setSelected] = useState(ranking.slice(0, 6));
  const rows = ((data.brand_trends as Record<string, unknown[]>)?.[lot] ?? []) as Record<string, unknown>[];

  return (
    <ChartBox>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}
      >
        <div
          style={{ fontSize: 14, fontWeight: 600, color: '#323232', display: 'flex', alignItems: 'center' }}
        >
          Brand Trends by Month (active patients)
          <InfoTip text={TIPS.brand} />
        </div>
        <LotPills value={lot} onChange={setLot} />
      </div>
      <MultiSelectChips items={ranking} selected={selected} onChange={setSelected} allItems={ranking} />
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={rows} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
          <XAxis dataKey="month" tick={{ fill: '#626262', fontSize: 10 }} />
          <YAxis tick={{ fill: '#626262', fontSize: 10 }} />
          <Tooltip contentStyle={tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {selected.map((brand) => (
            <Line
              key={brand}
              type="monotone"
              dataKey={brand}
              stroke={COLORS[ranking.indexOf(brand) % COLORS.length]}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </ChartBox>
  );
}

/* ── Regimen Share (stacked bar) ──────────────────────────────────────── */

function RegimenShareChart({ data }: { data: Record<string, unknown> }): React.JSX.Element {
  const [lot, setLot] = useState('all');
  const ranking = (data.regimen_ranking ?? []) as string[];
  const [selected, setSelected] = useState(ranking.slice(0, 6));
  const rawRows = ((data.regimen_share as Record<string, unknown[]>)?.[lot] ?? []) as Record<
    string,
    number
  >[];

  const rows = useMemo(
    () =>
      rawRows.map((r) => {
        const total = selected.reduce((s, k) => s + (r[k] ?? 0), 0);
        const out: Record<string, string | number> = { month: String(r.month ?? '') };
        selected.forEach((k) => {
          out[k] = total > 0 ? ((r[k] ?? 0) / total) * 100 : 0;
        });
        return out;
      }),
    [rawRows, selected]
  );

  return (
    <ChartBox>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}
      >
        <div
          style={{ fontSize: 14, fontWeight: 600, color: '#323232', display: 'flex', alignItems: 'center' }}
        >
          Regimen Share by Month
          <InfoTip text={TIPS.regimen} />
        </div>
        <LotPills value={lot} onChange={setLot} />
      </div>
      <MultiSelectChips items={ranking} selected={selected} onChange={setSelected} allItems={ranking} />
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={rows} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
          <XAxis dataKey="month" tick={{ fill: '#626262', fontSize: 10 }} />
          <YAxis domain={[0, 100]} tickFormatter={(v: number) => `${v.toFixed(0)}%`} tick={{ fill: '#626262', fontSize: 10 }} />
          <Tooltip contentStyle={tooltipStyle} formatter={(v: unknown) => (typeof v === 'number' ? `${v.toFixed(1)}%` : String(v)) as string} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {selected.map((reg) => (
            <Bar
              key={reg}
              dataKey={reg}
              stackId="stack"
              fill={COLORS[ranking.indexOf(reg) % COLORS.length]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartBox>
  );
}

/* ── IV vs SC (formulation stacked bar) ───────────────────────────────── */

function IVvsSCChart({ data }: { data: Record<string, unknown> }): React.JSX.Element {
  const brands = (data.iv_sc_brands ?? []) as string[];
  const forms = (data.iv_sc_forms ?? []) as string[];
  const [brand, setBrand] = useState(brands[0] ?? '');
  const rawRows = (data.iv_sc_distribution as Record<string, Record<string, number>[]>)?.[brand] ?? [];

  const rows = useMemo(
    () =>
      rawRows.map((r) => {
        const total = forms.reduce((s, k) => s + (r[k] ?? 0), 0);
        const out: Record<string, string | number> = { month: String(r.month ?? '') };
        forms.forEach((k) => {
          out[k] = total > 0 ? ((r[k] ?? 0) / total) * 100 : 0;
        });
        return out;
      }),
    [rawRows, forms]
  );

  return (
    <IVChartBox>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}
      >
        <div
          style={{ fontSize: 14, fontWeight: 600, color: '#323232', display: 'flex', alignItems: 'center' }}
        >
          Formulation Distribution by Month
          <InfoTip text={TIPS.formulation} />
        </div>
        <BrandSelect value={brand} onChange={(e) => setBrand(e.target.value)}>
          {brands.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </BrandSelect>
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={rows} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
            <XAxis dataKey="month" tick={{ fill: '#626262', fontSize: 10 }} />
            <YAxis
              domain={[0, 100]}
              tickFormatter={(v: number) => `${v.toFixed(0)}%`}
              tick={{ fill: '#626262', fontSize: 10 }}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(value: number | string | undefined) =>
                typeof value === 'number' ? `${value.toFixed(1)}%` : (value ?? '')
              }
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {forms.map((f, i) => (
              <Bar key={f} dataKey={f} stackId="stack" fill={FORM_COLORS[f] ?? COLORS[i % COLORS.length]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </IVChartBox>
  );
}

/* ── Sankey (custom SVG) ──────────────────────────────────────────────── */

interface SankeyNode {
  name: string;
}
interface SankeyLink {
  source: number;
  target: number;
  value: number;
}
interface SankeyData {
  nodes: SankeyNode[];
  links: SankeyLink[];
}
interface ComputedNode {
  name: string;
  x: number;
  y: number;
  height: number;
  value: number;
  color: string;
}
interface ComputedLink {
  source: number;
  target: number;
  value: number;
  sy: number;
  ty: number;
  thickness: number;
}

function getLotColor(name: string): string {
  if (name.startsWith('1L')) return LOT_COLORS['1L'];
  if (name.startsWith('2L')) return LOT_COLORS['2L'];
  if (name.startsWith('3L')) return LOT_COLORS['3L'];
  return LOT_COLORS['4L+'];
}

function SankeyChart({
  data,
  selectedRegimens,
}: {
  data: SankeyData;
  selectedRegimens: string[];
}): React.JSX.Element {
  const [hover, setHover] = useState<{
    x: number;
    y: number;
    label: string;
    sub: string;
    linkKey?: number;
  } | null>(null);

  const filtered = useMemo(() => {
    if (!data?.nodes?.length) return { nodes: [] as SankeyNode[], links: [] as SankeyLink[] };
    const sel = new Set(selectedRegimens);
    const keepNode = (name: string): boolean => {
      const colon = name.indexOf(':');
      const reg = colon >= 0 ? name.slice(colon + 1).trim() : name;
      return sel.has(reg);
    };
    const indexMap: Record<number, number> = {};
    const newNodes: SankeyNode[] = [];
    data.nodes.forEach((n, i) => {
      if (keepNode(n.name)) {
        indexMap[i] = newNodes.length;
        newNodes.push(n);
      }
    });
    const newLinks = data.links
      .filter((l) => indexMap[l.source] != null && indexMap[l.target] != null)
      .map((l) => ({ ...l, source: indexMap[l.source], target: indexMap[l.target] }));
    return { nodes: newNodes, links: newLinks };
  }, [data, selectedRegimens]);

  const { nodes, links } = useMemo((): { nodes: ComputedNode[]; links: ComputedLink[] } => {
    if (!filtered.nodes.length) return { nodes: [], links: [] };
    const W = 900,
      H = 450,
      pad = 80,
      nW = 16,
      nPad = 12;
    const columns: number[][] = [[], [], [], []];
    filtered.nodes.forEach((n, i) => {
      if (n.name.startsWith('1L')) columns[0].push(i);
      else if (n.name.startsWith('2L')) columns[1].push(i);
      else if (n.name.startsWith('3L')) columns[2].push(i);
      else columns[3].push(i);
    });
    const nv = new Array<number>(filtered.nodes.length).fill(0);
    filtered.links.forEach((l) => {
      nv[l.source] += l.value;
      nv[l.target] += l.value;
    });
    const colX = columns.map((_, i) => pad + (i * (W - 2 * pad - nW)) / Math.max(columns.length - 1, 1));
    const cn: ComputedNode[] = filtered.nodes.map((n, i) => ({
      name: n.name,
      x: 0,
      y: 0,
      height: 0,
      value: nv[i],
      color: getLotColor(n.name),
    }));
    columns.forEach((col, ci) => {
      const tv = col.reduce((s, ni) => s + nv[ni], 0) || 1;
      const ah = H - 2 * pad - Math.max(col.length - 1, 0) * nPad;
      const sc = ah / tv;
      let cy = pad;
      col.forEach((ni) => {
        const h = Math.max(nv[ni] * sc, 4);
        cn[ni].x = colX[ci];
        cn[ni].y = cy;
        cn[ni].height = h;
        cy += h + nPad;
      });
    });
    const so = new Array<number>(cn.length).fill(0);
    const to = new Array<number>(cn.length).fill(0);
    const cl: ComputedLink[] = filtered.links.map((l) => {
      const sN = cn[l.source],
        tN = cn[l.target];
      const sSc = sN.height / (nv[l.source] || 1),
        tSc = tN.height / (nv[l.target] || 1);
      const th = Math.max(l.value * Math.min(sSc, tSc), 2);
      const sy = sN.y + so[l.source],
        ty = tN.y + to[l.target];
      so[l.source] += th;
      to[l.target] += th;
      return { source: l.source, target: l.target, value: l.value, sy, ty, thickness: th };
    });
    return { nodes: cn, links: cl };
  }, [filtered]);

  if (!nodes.length)
    return (
      <p style={{ color: '#adadad', fontSize: 12, textAlign: 'center', padding: '32px 0' }}>
        No flow data — pick at least one regimen
      </p>
    );

  const nW = 16;
  const handleMove = (e: React.MouseEvent<SVGElement>, label: string, sub: string): void => {
    const svg = e.currentTarget.ownerSVGElement;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    setHover({ x: e.clientX - rect.left, y: e.clientY - rect.top, label, sub });
  };

  return (
    <div
      style={{
        width: '100%',
        overflowX: 'auto',
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <svg
        viewBox="0 0 900 450"
        style={{ maxWidth: 800, width: '100%', height: 'auto' }}
        onMouseLeave={() => setHover(null)}
      >
        {links.map((l, i) => {
          const sN = nodes[l.source],
            tN = nodes[l.target];
          const x0 = sN.x + nW,
            x1 = tN.x,
            mx = (x0 + x1) / 2;
          const path = `M ${x0} ${l.sy} C ${mx} ${l.sy}, ${mx} ${l.ty}, ${x1} ${l.ty} L ${x1} ${l.ty + l.thickness} C ${mx} ${l.ty + l.thickness}, ${mx} ${l.sy + l.thickness}, ${x0} ${l.sy + l.thickness} Z`;
          const isH = hover?.linkKey === i;
          return (
            <path
              key={i}
              d={path}
              fill={sN.color}
              fillOpacity={isH ? 0.55 : 0.2}
              stroke={sN.color}
              strokeOpacity={isH ? 0.7 : 0.3}
              strokeWidth={0.5}
              style={{ cursor: 'pointer' }}
              onMouseMove={(e) => {
                handleMove(e, `${sN.name} → ${tN.name}`, `${l.value.toLocaleString()} patients`);
                setHover((prev) => (prev ? { ...prev, linkKey: i } : null));
              }}
            />
          );
        })}
        {nodes.map((n, i) => (
          <g key={i}>
            <rect
              x={n.x}
              y={n.y}
              width={nW}
              height={n.height}
              fill={n.color}
              rx={3}
              style={{ cursor: 'pointer' }}
              onMouseMove={(e) => handleMove(e, n.name, `${n.value.toLocaleString()} patient touchpoints`)}
            />
            <text
              x={n.x < 450 ? n.x - 4 : n.x + nW + 4}
              y={n.y + n.height / 2}
              textAnchor={n.x < 450 ? 'end' : 'start'}
              dominantBaseline="central"
              fontSize={11}
              fontWeight={500}
              fill="#626262"
            >
              {n.name}
            </text>
          </g>
        ))}
      </svg>
      {hover && (
        <div
          style={{
            position: 'absolute',
            pointerEvents: 'none',
            background: '#fff',
            border: '1px solid #e5e5e5',
            borderRadius: 6,
            padding: '6px 10px',
            zIndex: 20,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            left: hover.x + 12,
            top: hover.y + 12,
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 12, color: '#323232' }}>{hover.label}</div>
          <div style={{ fontSize: 11, color: '#626262' }}>{hover.sub}</div>
        </div>
      )}
    </div>
  );
}

function SankeyCard({ data }: { data: SankeyData | undefined }): React.JSX.Element {
  const allRegimens = useMemo((): string[] => {
    if (!data?.nodes) return [];
    const seen = new Set<string>();
    data.nodes.forEach((n) => {
      const c = n.name.indexOf(':');
      seen.add(c >= 0 ? n.name.slice(c + 1).trim() : n.name);
    });
    return Array.from(seen);
  }, [data]);

  const defaultSel = useMemo((): string[] => {
    if (!data?.nodes) return [];
    const totals: Record<string, number> = {};
    const vbn = new Array<number>(data.nodes.length).fill(0);
    (data.links ?? []).forEach((l) => {
      vbn[l.source] += l.value;
      vbn[l.target] += l.value;
    });
    data.nodes.forEach((n, i) => {
      const c = n.name.indexOf(':');
      const r = c >= 0 ? n.name.slice(c + 1).trim() : n.name;
      totals[r] = (totals[r] ?? 0) + vbn[i];
    });
    return Object.entries(totals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([r]) => r);
  }, [data]);

  const [selected, setSelected] = useState<string[]>(defaultSel);
  useEffect(() => {
    setSelected(defaultSel);
  }, [defaultSel]);

  if (!data)
    return (
      <ChartBox>
        <p style={{ color: '#adadad', fontSize: 12, textAlign: 'center', padding: '32px 0' }}>
          No Sankey data
        </p>
      </ChartBox>
    );

  return (
    <ChartBox>
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: '#323232',
          marginBottom: 2,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        Treatment Regimen Flow (1L → 2L → 3L → 4L+)
        <InfoTip text={TIPS.sankey} />
      </div>
      <p style={{ fontSize: 10, color: '#adadad', marginBottom: 6, marginTop: 0 }}>
        Top 8 regimens per line; thickness = patient count. Hover any ribbon for detail.
      </p>
      <MultiSelectChips
        items={allRegimens}
        selected={selected}
        onChange={setSelected}
        allItems={allRegimens}
        max={20}
      />
      <SankeyChart data={data} selectedRegimens={selected} />
    </ChartBox>
  );
}

/* ── Main export ──────────────────────────────────────────────────────── */

interface TreatmentTrendsProps {
  data: Record<string, unknown> | undefined;
}

export default function TreatmentTrends({ data }: TreatmentTrendsProps): React.JSX.Element | null {
  if (!data) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <h2 style={{ fontSize: 14, fontWeight: 600, color: '#323232', margin: 0 }}>Treatment Trends</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <BrandTrendsChart data={data} />
        <RegimenShareChart data={data} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, alignItems: 'stretch' }}>
        <IVvsSCChart data={data} />
        <SankeyCard data={data.sankey as SankeyData | undefined} />
      </div>
    </div>
  );
}
